import React from 'react';
import { Box, Card, CardContent, Typography, alpha, Chip } from '@mui/material';
import { TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const SectorMomentumCards = ({ momentum }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  if (!momentum || (momentum.rising_sectors.length === 0 && momentum.falling_sectors.length === 0)) {
    return null;
  }

  // Helper function to get sector name based on current language
  const getSectorName = (sector) => {
    if (currentLang === 'vi' && sector.sector_name_vi) {
      return sector.sector_name_vi;
    }
    return sector.sector_name_en || sector.sector_name || sector.valuation_sector;
  };

  const SectorItem = ({ sector, type }) => {
    const isRising = type === 'rising';
    const color = isRising ? '#10b981' : '#ef4444';
    const bgColor = isRising ? alpha('#10b981', 0.08) : alpha('#ef4444', 0.08);
    const borderColor = isRising ? alpha('#10b981', 0.2) : alpha('#ef4444', 0.2);

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderRadius: 2,
          bgcolor: bgColor,
          border: `1px solid ${borderColor}`,
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: isRising ? alpha('#10b981', 0.12) : alpha('#ef4444', 0.12),
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${alpha(color, 0.15)}`,
          },
        }}
      >
        {/* Left: Sector Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 600,
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {getSectorName(sector)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`#${sector.ranking}`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                bgcolor: alpha(color, 0.15),
                color: color,
                '& .MuiChip-label': { px: 1 },
              }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {isRising ? '↑' : '↓'}{Math.abs(sector.rank_change)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              •
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('dashboard.momentum.score')}: {parseFloat(sector.final_score).toFixed(1)}
            </Typography>
          </Box>
        </Box>

        {/* Right: Score Change */}
        <Box sx={{ textAlign: 'right', ml: 2 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: color,
              lineHeight: 1,
            }}
          >
            {isRising ? '+' : ''}{parseFloat(sector.score_change).toFixed(2)}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      display: 'grid', 
      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
      gap: 3, 
      mb: 4,
    }}>
      {/* Rising Sectors */}
      <Card 
        elevation={0}
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            p: 2.5,
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#fff', 0.2),
              }}
            >
              <TrendingUpIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.25 }}>
                {t('dashboard.momentum.risingTitle')}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {t('dashboard.momentum.risingSubtitle')}
              </Typography>
            </Box>
            <Chip
              label={momentum.rising_sectors.length}
              sx={{
                bgcolor: alpha('#fff', 0.2),
                color: 'white',
                fontWeight: 700,
                fontSize: '0.875rem',
              }}
            />
          </Box>
        </Box>

        {/* Content */}
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {momentum.rising_sectors.slice(0, 6).map((sector) => (
              <SectorItem key={sector.sector_name} sector={sector} type="rising" />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Falling Sectors */}
      <Card 
        elevation={0}
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            p: 2.5,
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#fff', 0.2),
              }}
            >
              <TrendingDownIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.25 }}>
                {t('dashboard.momentum.fallingTitle')}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {t('dashboard.momentum.fallingSubtitle')}
              </Typography>
            </Box>
            <Chip
              label={momentum.falling_sectors.length}
              sx={{
                bgcolor: alpha('#fff', 0.2),
                color: 'white',
                fontWeight: 700,
                fontSize: '0.875rem',
              }}
            />
          </Box>
        </Box>

        {/* Content */}
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {momentum.falling_sectors.slice(0, 6).map((sector) => (
              <SectorItem key={sector.sector_name} sector={sector} type="falling" />
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SectorMomentumCards;
